import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, CalendarHeart, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError, getTracking } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const ROLES = ["The Groom", "Wedding Party", "Father of the Bride", "Father of the Groom", "Family & Guests", "Other"];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const PARTY_CARDS = [
  {
    title: "The Groom",
    text: "Bespoke wedding tuxedos, custom wedding suits and formal shirts made individually for the groom.",
    img: "/images/groom-suit.jpg",
  },
  {
    title: "The Wedding Party",
    text: "Coordinated tailoring for the best man, groomsmen and closest friends — cloth ordered together, cut from the same batch.",
    img: "/images/wedding-party.jpg",
  },
  {
    title: "Fathers & Family",
    text: "Suits and tuxedos for the father of the bride, the father of the groom and the wider family.",
    img: "/images/client-brown-suit.jpg",
    contain: true,
  },
];

const PACKAGES = [
  {
    id: "groom",
    name: "The Groom",
    price: "From $2,495",
    note: "The three-piece wedding suit",
    features: [
      "Three-piece made-to-measure suit",
      "Half-canvas construction",
      "Custom lining of your choosing",
      "Fine Italian fabrics",
      "Ready in six weeks",
    ],
  },
  {
    id: "groomsmen",
    name: "The Groomsmen Package",
    price: "$7,500",
    note: "For three adults",
    features: [
      "Three made-to-measure suits",
      "Three custom dress shirts",
      "Half-canvas construction",
      "Fine Italian fabrics",
      "Coordinated fittings so the party reads as one",
    ],
  },
];

export default function WeddingsPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "The Groom",
    party_size: 1, wedding_date: "", venue: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/wedding-inquiries", {
        ...form,
        party_size: Number(form.party_size) || 1,
        tracking: getTracking(),
      });
      setResult(data);
      toast.success("Your wedding inquiry has been received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFFFFF]" data-testid="weddings-page">
      <Navbar />

      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-20 sm:px-12 lg:grid-cols-12 lg:px-20 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#8C6D38]"
            data-testid="weddings-overline"
          >
            Bespoke Maestro Weddings · San Diego
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl font-light leading-[1.05] tracking-tight text-[#1A1917] sm:text-6xl lg:text-7xl">
            {["Your wedding.", "Your tuxedo.", "Your story."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#524F4A] sm:text-lg"
          >
            Bespoke and made-to-measure wedding suits and tuxedos for one of life's most important
            occasions. Exceptional Italian and English cloth, individual design, private fittings —
            for the groom, the wedding party and the fathers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href="#wedding-inquiry"
              data-testid="weddings-hero-cta"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("wedding-inquiry");
                if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFFFFF] transition-colors duration-200 hover:bg-[#8C6D38] active:scale-95"
            >
              Plan Your Wedding Wardrobe
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">
              From $2,495 · Ready in six weeks
            </p>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <img
              src="/images/wedding-couple.jpg"
              alt="Groom in a bespoke wedding tuxedo by Bespoke Maestro"
              className="h-[480px] w-full object-cover lg:h-[560px]"
              data-testid="weddings-hero-image"
            />
          </motion.div>
        </div>
      </section>

      {/* Who we dress */}
      <section className="border-y border-[#E5DEC9] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-12 lg:px-20">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">The Wedding Concierge</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
              One cloth, one vision — the whole party reads as one
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {PARTY_CARDS.map((c, i) => (
              <motion.div
                key={c.title}
                data-testid={`wedding-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
                whileHover={{ y: -4 }}
                className="group border border-[#E5DEC9] bg-[#FFFFFF]"
              >
                <div className="overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    className={`aspect-square w-full transition-transform duration-700 ease-out group-hover:scale-105 ${
                    c.contain ? "bg-white object-contain p-8" : "object-cover object-top"
                  }`}
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-medium text-[#1A1917]">{c.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#524F4A]">{c.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-12 grid gap-6 border border-[#E5DEC9] bg-[#FFFFFF] p-8 sm:grid-cols-3">
            <p className="flex items-start gap-3 text-sm font-light leading-relaxed text-[#524F4A]">
              <CalendarHeart size={16} className="mt-0.5 shrink-0 text-[#8C6D38]" />
              Begin eight to twelve weeks before the wedding — bespoke needs time to become perfect.
            </p>
            <p className="flex items-start gap-3 text-sm font-light leading-relaxed text-[#524F4A]">
              <Users size={16} className="mt-0.5 shrink-0 text-[#8C6D38]" />
              We coordinate groom, groomsmen, fathers and family — every garment cut from the same cloth batch.
            </p>
            <p className="flex items-start gap-3 text-sm font-light leading-relaxed text-[#524F4A]">
              <MapPin size={16} className="mt-0.5 shrink-0 text-[#8C6D38]" />
              Private consultations at your home, office, hotel or our San Diego studio. No rentals — every garment is yours.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-[#E5DEC9] bg-[#FFFFFF]" data-testid="wedding-pricing">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Wedding Packages</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
              Transparent pricing, uncompromising making
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-8 lg:grid-cols-2">
            {PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.id} delay={i * 0.12}>
                <div
                  data-testid={`wedding-package-${pkg.id}`}
                  className="flex h-full flex-col border border-[#E5DEC9] bg-white p-10 transition-colors duration-300 hover:border-[#1A1917]"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D38]">{pkg.note}</p>
                  <h3 className="mt-3 font-serif text-3xl font-medium text-[#1A1917]">{pkg.name}</h3>
                  <p className="mt-6 font-serif text-5xl font-light tracking-tight text-[#1A1917]">{pkg.price}</p>
                  <ul className="mt-8 flex-1 space-y-3 border-t border-[#EDE8DB] pt-8">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm font-light text-[#524F4A]">
                        <Check size={15} className="mt-0.5 shrink-0 text-[#C59B27]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#wedding-inquiry"
                    data-testid={`wedding-package-${pkg.id}-cta`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById("wedding-inquiry");
                      if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-10 inline-flex items-center justify-center gap-3 border border-[#1A1917] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1A1917] transition-colors duration-200 hover:bg-[#1A1917] hover:text-white active:scale-95"
                  >
                    Request This Package <ArrowRight size={14} />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">
              Larger parties and fathers &amp; family packages — ask during your consultation
            </p>
          </Reveal>
        </div>
      </section>

      {/* Inquiry form */}
      <section id="wedding-inquiry" className="mx-auto max-w-4xl px-6 py-24 sm:px-12 sm:py-32">
        {result ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="wedding-success"
            className="border border-[#E5DEC9] bg-white p-10 sm:p-14"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2D5A3F]">
              <Check size={22} className="text-white" />
            </div>
            <h2 className="mt-8 font-serif text-4xl font-light tracking-tight text-[#1A1917]">
              The concierge has your note.
            </h2>
            <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-[#524F4A]">
              Thank you, {result.name.split(" ")[0]}. Reza will reach out personally within one
              business day to begin planning the wedding wardrobe. A confirmation is on its way to
              your inbox.
            </p>
            <div className="mt-8 border-t border-[#EDE8DB] pt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Reference</p>
              <p className="mt-1 font-mono text-sm tracking-[0.15em] text-[#8C6D38]" data-testid="wedding-success-ref">
                {result.inquiry_id}
              </p>
            </div>
          </motion.div>
        ) : (
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Begin the Conversation</p>
            <h2 className="mt-4 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
              Tell us about the wedding
            </h2>
            <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#524F4A]">
              Share a few details and Reza will contact you personally to arrange a private
              consultation — for you, the wedding party and the family.
            </p>

            <form onSubmit={submit} className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="wedding-inquiry-form">
              <Field label="Full Name">
                <input data-testid="wedding-input-name" required value={form.name} onChange={set("name")} placeholder="James Sterling" className={inputCls} />
              </Field>
              <Field label="Email">
                <input data-testid="wedding-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </Field>
              <Field label="Phone">
                <input data-testid="wedding-input-phone" required value={form.phone} onChange={set("phone")} placeholder="(858) 555-0100" className={inputCls} />
              </Field>
              <Field label="Who is this for?">
                <select data-testid="wedding-input-role" value={form.role} onChange={set("role")} className={inputCls}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </Field>
              <Field label="Party Size (how many garments)">
                <input data-testid="wedding-input-party-size" type="number" min={1} max={50} value={form.party_size} onChange={set("party_size")} className={inputCls} />
              </Field>
              <Field label="Wedding Date (if set)">
                <input data-testid="wedding-input-date" type="date" value={form.wedding_date} onChange={set("wedding_date")} className={inputCls} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Venue or Location">
                  <input data-testid="wedding-input-venue" value={form.venue} onChange={set("venue")} placeholder="The Inn at Rancho Santa Fe, a La Jolla shoreline…" className={inputCls} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Anything else — colours, themes, timeline">
                  <textarea data-testid="wedding-input-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder="A coastal ceremony in October, midnight-blue tuxedos for six…" className={inputCls} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="wedding-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 bg-[#8C6D38] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#1A1917] active:scale-95 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Request Wedding Consultation"} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </section>

      <Footer />
    </div>
  );
}

const inputCls =
  "w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</span>
      {children}
    </label>
  );
}
